import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { SiteFormValues } from '../siteSchema';
import { ProjectFullDto } from '../siteApi';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import TimezonePicker from '@/components/ui/timezone-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AddressSearch } from '@/components/maps/AddressSearch';
import { GeocodingResult, getTimezoneFromCoordinates } from '@/components/maps/mapbox';
import SiteLocationMap from '@/components/maps/SiteLocationMap';

interface PendingAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postCode: string;
  country: string;
  lat: number;
  lon: number;
}

interface SiteDetailsEditorProps {
  form: UseFormReturn<SiteFormValues>;
  site: ProjectFullDto | undefined;
}

export function SiteDetailsEditor({ form, site }: SiteDetailsEditorProps) {
  const [locationType, setLocationType] = useState<'address' | 'coordinates'>('address');
  const [mapLat, setMapLat] = useState(-36.86008);
  const [mapLon, setMapLon] = useState(174.760373);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [pendingAddress, setPendingAddress] = useState<PendingAddress | null>(null);
  const [pendingMapLat, setPendingMapLat] = useState(-36.86008);
  const [pendingMapLon, setPendingMapLon] = useState(174.760373);
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null);

  // Watch address fields for display
  const addressLine1 = form.watch('address.addressLine1');
  const city = form.watch('address.city');
  const region = form.watch('address.region');
  const postCode = form.watch('address.postCode');
  const country = form.watch('address.country');

  // Initialize map coordinates from site data
  useEffect(() => {
    if (!site) return;

    setMapLat(site.address?.lat || -36.86008);
    setMapLon(site.address?.lon || 174.760373);

    // Initialize pending coordinates to match current values
    setPendingMapLat(site.address?.lat || -36.86008);
    setPendingMapLon(site.address?.lon || 174.760373);

    // Set location type based on address data
    if (site.address?.addressLine1) {
      setLocationType('address');
    } else if (site.address?.lat && site.address?.lon) {
      setLocationType('coordinates');
    }
  }, [site]);

  // Initialize pending coordinates when modal opens
  useEffect(() => {
    if (isAddressModalOpen) {
      const currentLat = form.getValues('address.lat') || mapLat;
      const currentLon = form.getValues('address.lon') || mapLon;
      setPendingMapLat(currentLat);
      setPendingMapLon(currentLon);
      setPendingAddress(null); // Reset pending changes
    }
  }, [isAddressModalOpen, form, mapLat, mapLon]);

  const handleAddressSelect = (result: GeocodingResult) => {
    // Store pending address changes instead of immediately updating form
    setPendingAddress({
      addressLine1: result.address || '',
      addressLine2: result.address_line2 || '',
      city: result.city || '',
      region: result.state || '',
      postCode: result.postcode || '',
      country: result.country || '',
      lat: result.center[1],
      lon: result.center[0],
    });
    setPendingMapLat(result.center[1]);
    setPendingMapLon(result.center[0]);

    // Detect timezone mismatch
    const timezone =
      result.timezone || getTimezoneFromCoordinates(result.center[0], result.center[1]);
    const currentTimezone = form.getValues('timeZone');
    if (timezone && currentTimezone && currentTimezone !== timezone) {
      setDetectedTimezone(timezone);
    } else if (timezone) {
      setDetectedTimezone(null);
    }
  };

  const handleMapCoordinatesChange = (coords: { lat: number; lng: number }) => {
    setPendingAddress((prev) =>
      prev
        ? { ...prev, lat: coords.lat, lon: coords.lng }
        : {
            addressLine1: '',
            addressLine2: '',
            city: '',
            region: '',
            postCode: '',
            country: '',
            lat: coords.lat,
            lon: coords.lng,
          }
    );
    setPendingMapLat(coords.lat);
    setPendingMapLon(coords.lng);

    // Detect timezone mismatch
    const timezone = getTimezoneFromCoordinates(coords.lng, coords.lat);
    const currentTimezone = form.getValues('timeZone');
    if (timezone && currentTimezone && currentTimezone !== timezone) {
      setDetectedTimezone(timezone);
    } else if (timezone) {
      setDetectedTimezone(null);
    }
  };

  const handleMapAddressChange = (address: string, lat: number, lng: number) => {
    setPendingAddress((prev) =>
      prev
        ? { ...prev, addressLine1: address, lat, lon: lng }
        : {
            addressLine1: address,
            addressLine2: '',
            city: '',
            region: '',
            postCode: '',
            country: '',
            lat: lat,
            lon: lng,
          }
    );
    setPendingMapLat(lat);
    setPendingMapLon(lng);

    // Detect timezone mismatch
    const timezone = getTimezoneFromCoordinates(lng, lat);
    const currentTimezone = form.getValues('timeZone');
    if (timezone && currentTimezone && currentTimezone !== timezone) {
      setDetectedTimezone(timezone);
    } else if (timezone) {
      setDetectedTimezone(null);
    }
  };

  const handleCancel = () => {
    // Reset pending changes and close modal
    setPendingAddress(null);
    setPendingMapLat(mapLat);
    setPendingMapLon(mapLon);
    setDetectedTimezone(null);
    setIsAddressModalOpen(false);
  };

  const applyPendingChanges = (updateTimezone: boolean) => {
    if (!pendingAddress) return;

    form.setValue('address.addressLine1', pendingAddress.addressLine1);
    form.setValue('address.addressLine2', pendingAddress.addressLine2);
    form.setValue('address.city', pendingAddress.city);
    form.setValue('address.region', pendingAddress.region);
    form.setValue('address.postCode', pendingAddress.postCode);
    form.setValue('address.country', pendingAddress.country);
    form.setValue('address.lat', pendingAddress.lat);
    form.setValue('address.lon', pendingAddress.lon);
    setMapLat(pendingAddress.lat);
    setMapLon(pendingAddress.lon);

    if (updateTimezone && detectedTimezone) {
      form.setValue('timeZone', detectedTimezone);
    }

    // Clear pending changes and close modal
    setPendingAddress(null);
    setDetectedTimezone(null);
    setIsAddressModalOpen(false);
  };

  return (
    <div className="mb-6 grid scroll-mt-[8rem] gap-6" id="site-details">
      <div className="overflow-hidden rounded-lg border bg-[#111] text-white">
        <div className="mb-4 border-b p-4">
          <h2 className="text-xl font-semibold">Site Details</h2>
        </div>
        <section className="grid grid-cols-2">
          <div className="mb-4 overflow-hidden rounded-lg px-4">
            <SiteLocationMap
              coordinates={{
                lat: mapLat,
                lng: mapLon,
              }}
              onCoordinatesChange={(coords) => {
                form.setValue('address.lat', coords.lat);
                form.setValue('address.lon', coords.lng);
                setMapLat(coords.lat);
                setMapLon(coords.lng);
              }}
              onAddressChange={(address, lat, lng) => {
                form.setValue('address.addressLine1', address);
                form.setValue('address.lat', lat);
                form.setValue('address.lon', lng);
                setMapLat(lat);
                setMapLon(lng);
              }}
            />
          </div>
          <section>
            <p className="px-4 pb-4 text-sm">
              How should the AI and monitoring agents refer to this site?
            </p>
            <div className="flex gap-4 px-4 pb-0">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Site Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="timeZone"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Time Zone</FormLabel>
                    <FormControl>
                      <TimezonePicker field={field} form={form} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <Dialog open={isAddressModalOpen} onOpenChange={setIsAddressModalOpen}>
                  <DialogTrigger asChild>
                    <div>
                      <p className="mb-2 text-sm">Site Address</p>
                      <div className="cursor-pointer rounded-md border bg-[#111] p-3 hover:bg-[#2a2a2a]">
                        {addressLine1 || city || region || postCode || country ? (
                          [
                            addressLine1,
                            [city, region, postCode, country].filter(Boolean).join(', '),
                          ]
                            .filter(Boolean)
                            .join(' - ')
                        ) : (
                          <span className="text-gray-400">Set site location</span>
                        )}
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>Edit site location</DialogTitle>
                    </DialogHeader>
                    <div className="p-4 pt-0">
                      <div className="mt-4 space-y-4">
                        <div className="grid w-full gap-4">
                          <label>Location Type</label>
                          <RadioGroup
                            value={locationType}
                            onValueChange={(value: 'address' | 'coordinates') => {
                              setLocationType(value);
                            }}
                            className="mb-4 grid grid-cols-2 gap-4"
                          >
                            <label
                              htmlFor="address"
                              className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                                locationType === 'address'
                                  ? 'border-lime bg-lime/10'
                                  : 'border-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="address" id="address" />
                                <span className="font-semibold text-lime">Address</span>
                              </div>
                              <p className="mt-2 text-gray-400">
                                Use a street address to locate the site
                              </p>
                            </label>
                            <label
                              htmlFor="coordinates"
                              className={`flex cursor-pointer flex-col rounded-lg border p-4 ${
                                locationType === 'coordinates'
                                  ? 'border-lime bg-lime/10'
                                  : 'border-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="coordinates" id="coordinates" />
                                <span className="font-semibold text-lime">Coordinates</span>
                              </div>
                              <p className="mt-2 text-gray-400">
                                Use latitude and longitude coordinates
                              </p>
                            </label>
                          </RadioGroup>
                        </div>
                        <div className="space-y-4">
                          <div className={` ${locationType === 'address' ? '' : 'hidden'}`}>
                            <label className="pb-2 text-sm">Address</label>
                            <AddressSearch
                              onSelect={handleAddressSelect}
                              placeholder="Search for an address"
                            />
                          </div>
                          <div
                            className={`mt-4 flex flex-wrap gap-4 ${locationType === 'coordinates' ? '' : 'hidden'}`}
                          >
                            <div className="grid flex-1 items-center gap-1.5">
                              <FormField
                                control={form.control}
                                name="address.lat"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Latitude</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        step="any"
                                        hidden={locationType === 'address'}
                                        value={pendingAddress?.lat ?? (field.value as number)}
                                        onChange={(e) => {
                                          const lat = parseFloat(e.target.value);
                                          if (!isNaN(lat)) {
                                            const currentLon =
                                              pendingAddress?.lon ?? (field.value as number) ?? 0;
                                            setPendingAddress((prev) =>
                                              prev
                                                ? { ...prev, lat }
                                                : {
                                                    addressLine1: '',
                                                    addressLine2: '',
                                                    city: '',
                                                    region: '',
                                                    postCode: '',
                                                    country: '',
                                                    lat,
                                                    lon: currentLon,
                                                  }
                                            );
                                            setPendingMapLat(lat);
                                          }
                                        }}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="grid flex-1 items-center gap-1.5">
                              <FormField
                                control={form.control}
                                name="address.lon"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Longitude</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        step="any"
                                        hidden={locationType === 'address'}
                                        value={pendingAddress?.lon ?? (field.value as number)}
                                        onChange={(e) => {
                                          const lng = parseFloat(e.target.value);
                                          if (!isNaN(lng)) {
                                            const currentLat =
                                              pendingAddress?.lat ?? (field.value as number) ?? 0;
                                            setPendingAddress((prev) =>
                                              prev
                                                ? { ...prev, lon: lng }
                                                : {
                                                    addressLine1: '',
                                                    addressLine2: '',
                                                    city: '',
                                                    region: '',
                                                    postCode: '',
                                                    country: '',
                                                    lat: currentLat,
                                                    lon: lng,
                                                  }
                                            );
                                            setPendingMapLon(lng);
                                          }
                                        }}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="h-[300px] overflow-hidden rounded-lg">
                          <SiteLocationMap
                            coordinates={{
                              lat: pendingMapLat,
                              lng: pendingMapLon,
                            }}
                            onCoordinatesChange={handleMapCoordinatesChange}
                            onAddressChange={handleMapAddressChange}
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <div className="flex w-full gap-2">
                        <Button type="button" variant="outline" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <div className="flex-grow" />
                        <Button
                          type="button"
                          variant={detectedTimezone ? 'outline' : 'default'}
                          onClick={() => applyPendingChanges(false)}
                          disabled={!pendingAddress}
                          >
                          {detectedTimezone ? 'Keep Current Timezone' : 'Confirm Location'}
                        </Button>
                        {detectedTimezone && (
                          <Button
                            type="button"
                            onClick={() => applyPendingChanges(true)}
                            disabled={!pendingAddress}
                          >
                            Update Location & Timezone
                          </Button>
                        )}
                      </div>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}
